// src/components/ThemeToggle.jsx
import React, { useState, useRef, useEffect } from 'react';
import { useTheme } from './ThemeContext'
import { Sun, Moon, Palette, Check, ChevronDown } from 'lucide-react';

const ThemeToggle = ({ variant = 'dropdown', className = '' }) => {
  const { theme, currentTheme, setTheme, themes } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Theme icons mapping
  const getThemeIcon = (themeId) => {
    switch(themeId) {
      case 'dark': return <Moon className="w-4 h-4" />;
      case 'light': return <Sun className="w-4 h-4" />;
      default: return <Palette className="w-4 h-4" />;
    }
  };

  if (variant === 'simple') {
    return (
      <button
        onClick={() => setTheme(currentTheme === 'dark' ? 'light' : 'dark')}
        className={`p-2 rounded-lg transition-all ${className}`}
        style={{ 
          backgroundColor: `${theme.secondary}20`,
          color: theme.accent1
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = `${theme.hover}20`;
          e.currentTarget.style.color = theme.hover;
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = `${theme.secondary}20`;
          e.currentTarget.style.color = theme.accent1;
        }}
      >
        {currentTheme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
      </button>
    );
  }

  if (variant === 'minimal') {
    return (
      <div className="flex items-center gap-1">
        {themes.map((t) => (
          <button
            key={t.id}
            onClick={() => setTheme(t.id)}
            className="w-8 h-8 rounded-full transition-all transform hover:scale-110"
            style={{ 
              backgroundColor: t.colors.accent2,
              border: currentTheme === t.id ? `2px solid ${theme.accent1}` : 'none',
              boxShadow: currentTheme === t.id ? `0 0 0 2px ${theme.accent1}40` : 'none'
            }}
            title={t.name}
          />
        ))}
      </div>
    );
  }

  // Default dropdown variant
  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-all ${className}`}
        style={{ 
          backgroundColor: `${theme.secondary}20`,
          color: theme.accent1,
          border: `1px solid ${theme.accent2}30`
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = `${theme.hover}20`;
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = `${theme.secondary}20`;
        }}
      >
        {getThemeIcon(currentTheme)}
        <span className="text-sm">{theme.name}</span>
        <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div 
          className="absolute right-0 mt-2 w-48 rounded-lg shadow-lg overflow-hidden z-50"
          style={{ 
            backgroundColor: theme.surface,
            border: `1px solid ${theme.border}`,
            boxShadow: `0 10px 25px -5px ${theme.primary}40`
          }}
        >
          {themes.map((t) => (
            <button
              key={t.id}
              onClick={() => {
                setTheme(t.id);
                setIsOpen(false);
              }}
              className="w-full flex items-center justify-between px-4 py-3 transition-all"
              style={{ 
                color: theme.text,
                backgroundColor: currentTheme === t.id ? `${theme.accent2}20` : 'transparent'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = `${theme.hover}20`;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = currentTheme === t.id ? `${theme.accent2}20` : 'transparent';
              }}
            >
              <div className="flex items-center gap-3">
                <div className="w-4 h-4 rounded-full" style={{ backgroundColor: t.colors.accent2 }} />
                <span className="text-sm">{t.name}</span>
              </div>
              {currentTheme === t.id && (
                <Check className="w-4 h-4" style={{ color: theme.accent2 }} />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default ThemeToggle;